/**
 * Catálogo de ubigeo del Perú (departamento / provincia / distrito).
 *
 * El panel anterior cargaba `js/ubigeo-peru.json` desde la raíz del panel
 * (`Empresa.php` → `get_renderizar_departamentos()`). EDSuite Web incluye una
 * copia del mismo archivo en `public/js/`, así que un solo `fetch` —cacheado en
 * memoria— alimenta los `select` de cualquier pantalla sin depender de un
 * servicio externo.
 *
 * Lo usan Configuración → Empresa (dirección fiscal) y Pricing → Scraping.
 */

export interface OpcionUbigeo {
  id: string;
  name: string;
}

interface DatosUbigeo {
  departamentos: OpcionUbigeo[];
  /** Provincias indexadas por código de departamento. */
  provincias: Record<string, OpcionUbigeo[]>;
  /** Distritos indexados por código de provincia. */
  distritos: Record<string, OpcionUbigeo[]>;
}

const RUTA = '/js/ubigeo-peru.json';

let promesa: Promise<DatosUbigeo> | null = null;

/** Carga (una sola vez) el catálogo de ubigeo. */
export function cargarUbigeo(): Promise<DatosUbigeo> {
  if (!promesa) {
    promesa = fetch(RUTA)
      .then((respuesta) => {
        if (!respuesta.ok) throw new Error(`No se pudo cargar el ubigeo (HTTP ${respuesta.status}).`);
        return respuesta.json() as Promise<DatosUbigeo>;
      })
      .catch((error: unknown) => {
        promesa = null; // permite reintentar en la próxima apertura
        throw error;
      });
  }

  return promesa;
}

/** Provincias de un departamento. */
export async function provinciasDe(departamentoId: string): Promise<OpcionUbigeo[]> {
  if (!departamentoId) return [];

  const datos = await cargarUbigeo();
  return datos.provincias[departamentoId] ?? [];
}

/** Distritos de una provincia. */
export async function distritosDe(provinciaId: string): Promise<OpcionUbigeo[]> {
  if (!provinciaId) return [];

  const datos = await cargarUbigeo();
  return datos.distritos[provinciaId] ?? [];
}

/**
 * Código de ubigeo del distrito (los ids del catálogo ya son el ubigeo de
 * 6 dígitos: `140309` = Lambayeque · Lambayeque · Pacora).
 */
export function codigoUbigeoDe(distritoId: string): string {
  return distritoId;
}
